// 产品矩阵系统 - 产品服务层
import { supabase } from '../../config/supabase';
import { uploadToR2, deleteFromR2, getPublicUrl } from '../../config/storage';
import type { Product, ProductModel, ProductCategory, ProductFilter, PaginatedResponse } from '../types';

// =====================================================
// 产品 CRUD 操作
// =====================================================

/** 获取产品列表 */
export async function fetchProducts(
  filter?: ProductFilter,
  page = 1,
  pageSize = 50
): Promise<PaginatedResponse<Product>> {
  let query = supabase
    .from('ditu_products')
    .select(`
      *,
      models:ditu_product_models(*)
    `, { count: 'exact' })
    .eq('is_active', true)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });

  // 应用筛选条件
  if (filter?.category && filter.category !== 'all') {
    query = query.eq('category', filter.category);
  }

  if (filter?.search) {
    query = query.or(`name.ilike.%${filter.search}%,name_en.ilike.%${filter.search}%,slogan.ilike.%${filter.search}%`);
  }

  if (filter?.processType) {
    query = query.eq('process_type', filter.processType);
  }

  // 分页
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) throw error;

  return {
    data: (data || []) as Product[],
    total: count || 0,
    page,
    pageSize,
    hasMore: (count || 0) > page * pageSize,
  };
}

/** 获取单个产品详情 */
export async function fetchProductById(id: string): Promise<Product | null> {
  const { data, error } = await supabase
    .from('ditu_products')
    .select(`
      *,
      models:ditu_product_models(*)
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    throw error;
  }

  return data as Product;
}

/** 创建产品 */
export async function createProduct(product: Partial<Product>): Promise<Product> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('ditu_products')
    .insert({
      ...product,
      created_by: user.id,
      updated_by: user.id,
    })
    .select()
    .single();

  if (error) throw error;
  return data as Product;
}

/** 更新产品 */
export async function updateProduct(id: string, updates: Partial<Product>): Promise<Product> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('ditu_products')
    .update({
      ...updates,
      updated_by: user.id,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Product;
}

/** 删除产品 (软删除，设为不活跃) */
export async function deleteProduct(id: string): Promise<void> {
  const { error } = await supabase
    .from('ditu_products')
    .update({ is_active: false })
    .eq('id', id);

  if (error) throw error;
}

/** 硬删除产品 */
export async function hardDeleteProduct(id: string): Promise<void> {
  // 先获取产品信息，删除关联的图片
  const product = await fetchProductById(id);
  if (product?.image_storage_key) {
    try {
      await deleteFromR2(product.image_storage_key);
    } catch (e) {
      console.warn('Failed to delete product image from R2:', e);
    }
  }

  const { error } = await supabase
    .from('ditu_products')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// =====================================================
// 产品型号 CRUD 操作
// =====================================================

/** 获取产品的所有型号 */
export async function fetchProductModels(productId: string): Promise<ProductModel[]> {
  const { data, error } = await supabase
    .from('ditu_product_models')
    .select('*')
    .eq('product_id', productId)
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data || []) as ProductModel[];
}

/** 创建产品型号 */
export async function createProductModel(model: Partial<ProductModel>): Promise<ProductModel> {
  const { data, error } = await supabase
    .from('ditu_product_models')
    .insert(model)
    .select()
    .single();

  if (error) throw error;
  return data as ProductModel;
}

/** 更新产品型号 */
export async function updateProductModel(id: string, updates: Partial<ProductModel>): Promise<ProductModel> {
  const { data, error } = await supabase
    .from('ditu_product_models')
    .update({
      ...updates,
      updated_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as ProductModel;
}

/** 删除产品型号 */
export async function deleteProductModel(id: string): Promise<void> {
  const { error } = await supabase
    .from('ditu_product_models')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// =====================================================
// 图片上传
// =====================================================

/** 上传产品图片 */
export async function uploadProductImage(
  file: File,
  productId?: string
): Promise<{ url: string; storageKey: string }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const timestamp = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const storageKey = `poster/products/${productId || 'temp'}/${timestamp}-${safeName}`;

  await uploadToR2(file, storageKey);
  const url = await getPublicUrl(storageKey);

  return { url, storageKey };
}

/** 删除产品图片 */
export async function deleteProductImage(storageKey: string): Promise<void> {
  await deleteFromR2(storageKey);
}

// =====================================================
// 产品收藏
// =====================================================

/** 获取用户收藏的产品 */
export async function fetchFavoriteProducts(): Promise<Product[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from('ditu_product_favorites')
    .select(`
      product:ditu_products(
        *,
        models:ditu_product_models(*)
      )
    `)
    .eq('user_id', user.id);

  if (error) throw error;
  return (data || []).map((d: any) => d.product).filter(Boolean) as Product[];
}

/** 添加产品收藏 */
export async function addProductFavorite(productId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase
    .from('ditu_product_favorites')
    .insert({
      user_id: user.id,
      product_id: productId,
    });

  if (error && error.code !== '23505') throw error; // 忽略重复插入错误
}

/** 移除产品收藏 */
export async function removeProductFavorite(productId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase
    .from('ditu_product_favorites')
    .delete()
    .eq('user_id', user.id)
    .eq('product_id', productId);

  if (error) throw error;
}

/** 检查产品是否已收藏 */
export async function isProductFavorited(productId: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { data, error } = await supabase
    .from('ditu_product_favorites')
    .select('id')
    .eq('user_id', user.id)
    .eq('product_id', productId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return !!data;
}

// =====================================================
// 统计和其他
// =====================================================

/** 获取产品分类统计 */
export async function getProductCategoryStats(): Promise<Record<ProductCategory | 'all', number>> {
  const { data, error } = await supabase
    .from('ditu_products')
    .select('category')
    .eq('is_active', true);

  if (error) throw error;

  const stats: Record<string, number> = {
    all: data?.length || 0,
    all_in_one: 0,
    product: 0,
    module: 0,
  };

  (data || []).forEach((p: any) => {
    if (p.category && stats.hasOwnProperty(p.category)) {
      stats[p.category]++;
    }
  });

  return stats as Record<ProductCategory | 'all', number>;
}

/** 更新产品排序 */
export async function updateProductSortOrder(productIds: string[]): Promise<void> {
  const updates = productIds.map((id, index) => ({
    id,
    sort_order: index,
  }));

  for (const update of updates) {
    const { error } = await supabase
      .from('ditu_products')
      .update({ sort_order: update.sort_order })
      .eq('id', update.id);

    if (error) throw error;
  }
}
